import { computed, ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import apiInterceptor from '@/shared/apiInterceptor';
import { environment } from '@/shared/env';
import { queryClient } from '@/shared/queryClient';

export const customDataFieldsQueryOptions = {
  queryKey: ['custom-fields'],
  queryFn: async () => {
    const r = await apiInterceptor.get(environment('VUE_APP_API_URL') + 'crm/v1/customers/custom-fields');
    const fields = r?.custom_fields ?? r?.data?.custom_fields;
    return Array.isArray(fields) ? fields : [];
  },
  staleTime: Infinity,
};

export function useCustomDataFields() {
  const query = useQuery(customDataFieldsQueryOptions);
  
  // Create a reactive ref that syncs with cache
  const cachedFields = ref([]);
  
  // Initialize from cache
  const initializeFromCache = () => {
    cachedFields.value = queryClient.getQueryData(['custom-fields']) ?? [];
  };
  
  // Subscribe to cache changes to keep cachedFields in sync
  const unsubscribe = queryClient.getQueryCache().subscribe(() => {
    initializeFromCache();
  });
  
  // Initialize on first use
  initializeFromCache();

  // Return standard TanStack Query pattern with reactive data property
  // The data property is a ref that contains the array directly (not wrapped in .value)
  // so components that expect query.data.value still work correctly
  return {
    // Use cachedFields as the reactive source, with fallback to query data
    data: computed(() => {
      if (cachedFields.value.length > 0) {
        return cachedFields.value;
      }
      // Safely access query.data which is a Ref
      return query.data.value ?? [];
    }),
    isPending: query.isLoading,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    refetch: query.refetch,
    unsubscribe, // Return unsubscribe so components can cleanup
  };
}

/**
 * Optimistically adds a new custom field to the cache immediately for instant UI feedback.
 * The field is added to the existing cached list without refetching.
 * Use this for immediate UX response when a new field is created.
 */
export function optimisticallyAddCustomField(newField) {
  const queryKey = ['custom-fields'];
  const currentData = queryClient.getQueryData(queryKey) ?? [];
  
  // Extract field name if object, otherwise use as string
  const fieldName = typeof newField === 'string' ? newField : newField?.name || newField?.identifier;
  
  // Only add if it doesn't already exist
  const fieldExists = currentData.includes(fieldName);
  
  if (!fieldExists) {
    const updatedData = [...currentData, fieldName];
    queryClient.setQueryData(queryKey, updatedData);
  }
}

/**
 * Clears the custom fields cache and refetches fresh data from the API.
 * Call this after custom fields are created/modified to ensure all components see updated list.
 * Usually called after optimisticallyAddCustomField() to validate and sync with server.
 */
export async function invalidateAndRefetchCustomDataFields() {
  queryClient.removeQueries({ queryKey: ['custom-fields'] });
  await queryClient.ensureQueryData(customDataFieldsQueryOptions);
}

/**
 * Handles cache invalidation for custom fields based on rule operations.
 * Optimistically updates cache for immediate UI feedback, then refetches in background.
 * @param {Object} rule - The rule object containing operations
 */
export function handleCustomFieldsCacheInvalidation(rule) {
  const hasCustomFieldOperation = rule.operations?.some(op =>
    op.key === 'create_crm_custom_field' || op.key === 'remove_crm_custom_field'
  );

  if (!hasCustomFieldOperation) return;

  // Extract field data from the rule operations
  rule.operations?.forEach(operation => {
    if (operation.key === 'create_crm_custom_field') {
      const fieldNameInput = operation.input_fields_required?.find(e => e.identifier === 'field_name');
      
      if (fieldNameInput?.modelValue) {
        // Optimistically add only the field name string to cache
        optimisticallyAddCustomField(fieldNameInput.modelValue);
      }
    }
  });
  
  // Trigger full refetch in background for validation
  setTimeout(() => {
    invalidateAndRefetchCustomDataFields();
  }, 100);
}
